import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { security_settings } from "./BaseURL";
import { logoutUser } from "./services/socket";

// ============================================================================
//  IdleLogout — sign a workstation out after a period of inactivity
// ============================================================================
//
//  The timeout is a FACILITY SETTING now, in minutes, read from
//  GET /system/security-settings and edited in
//  Admin Panel → System Settings → Session & Security.
//
//  It used to be `timeout={10 * 60 * 1000}`, written into App.js and compiled
//  into the bundle. Ten minutes suits a shared reception terminal in an open
//  waiting room; it does not suit a consulting room where a doctor is with one
//  patient for forty minutes, and being signed out mid-consultation is how
//  staff end up defeating the timeout altogether.
//
//  Two things were also wrong with this component, both fixed here, and both
//  worth stating plainly because they meant the feature did not work:
//
//    1. IT DID NOT LOG ANYBODY OUT. On timeout it called
//       `localStorage.removeItem("user")` — but the session is stored under
//       `userInfo`. So it navigated to "/" with the session intact: pressing
//       Back, or typing a /dashboard URL, put you straight back in, and the
//       socket still had you online. Real logout (MainLayout's handleLogout)
//       clears `userInfo` AND tells the socket. This now does the same.
//
//    2. The activity listeners were registered with an empty dependency
//       array, so the closure kept whatever `timeout` was on first render.
//       That did not matter when the value was a constant. It matters
//       completely once the value arrives from a fetch, which is exactly what
//       making it configurable does — the component would have gone on using
//       the fallback forever.

const DEFAULT_MINUTES = 10;
const MIN_MINUTES     = 1;
const MAX_MINUTES     = 480;

// Dispatched by the settings screen after a successful save so the admin who
// just changed the value sees it take effect immediately, rather than on their
// next page load.
export const IDLE_TIMEOUT_CHANGED_EVENT = "mefims:idle-timeout-changed";

const clampMinutes = (v) => {
  const n = parseInt(v, 10);
  if (!Number.isFinite(n)) return DEFAULT_MINUTES;
  return Math.min(Math.max(n, MIN_MINUTES), MAX_MINUTES);
};

export default function IdleLogout({ timeout, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const timer = useRef(null);
  const locationRef = useRef(location);
  locationRef.current = location;

  // `timeout` (ms) stays supported as an override — some tests and any caller
  // that wants a fixed value can still pass one, and it wins over the setting.
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const effectiveMs = Number.isFinite(timeout) && timeout > 0
    ? timeout
    : minutes * 60 * 1000;

  // ── Load the facility's setting ───────────────────────────────────────────
  //
  //  Failure is deliberately quiet and falls back to the ten-minute default.
  //  The safe direction to fail for a session policy is the SHORTER timeout —
  //  a network blip must never turn into a session that does not expire.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await axios.get(security_settings, { timeout: 8000 });
        if (!cancelled && data?.success) {
          setMinutes(clampMinutes(data.data?.idleTimeoutMinutes));
        }
      } catch (_) {
        // Older backend, or unreachable. Keep the default.
      }
    };

    load();
    window.addEventListener(IDLE_TIMEOUT_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(IDLE_TIMEOUT_CHANGED_EVENT, load);
    };
  }, []);

  // ── The logout itself ─────────────────────────────────────────────────────
  const signOut = useCallback(() => {
    let userInfo = null;
    try {
      const raw = localStorage.getItem("userInfo");
      userInfo = raw ? JSON.parse(raw) : null;
    } catch (_) { /* corrupt value — fall through and clear it anyway */ }

    // Nobody is signed in, so there is no session to end — and navigating
    // anyway would do real damage. This component wraps the whole app,
    // including the first-run Setup wizard: an installer who spends ten
    // minutes on the facility form without touching the mouse would have been
    // thrown back to the start of the wizard with everything they had typed
    // discarded.
    if (!userInfo?.username && !userInfo?.trans_code) return;

    // Remember where they were, so the login screen can offer to resume.
    try {
      const path = locationRef.current?.pathname || "";
      const isPanel = path.startsWith("/dashboard/") && path !== "/dashboard/";
      if (userInfo?.username && isPanel) {
        localStorage.setItem(
          "lastSession",
          JSON.stringify({
            username: userInfo.username,
            route: path,
            logoutTime: Date.now(),
            logoutDate: new Date().toDateString(),
          })
        );
      } else {
        localStorage.removeItem("lastSession");
      }
    } catch (_) { /* ignore — never block the logout on bookkeeping */ }

    // Tell the server we are gone, so this user stops showing as online to
    // everyone else in the staff chat.
    try {
      if (userInfo?.trans_code) logoutUser(String(userInfo.trans_code));
    } catch (_) { /* the local clear below is what actually matters */ }

    // The actual sign-out. `userInfo` is the session key — clearing "user",
    // as this used to, cleared nothing at all.
    localStorage.removeItem("userInfo");
    localStorage.removeItem("user");   // legacy key, harmless to clear too
    sessionStorage.clear();

    navigate("/", { replace: true });
  }, [navigate]);

  // ── Timer ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const resetTimer = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(signOut, effectiveMs);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "click", "scroll"];
    // `passive` on the two high-frequency ones: neither handler calls
    // preventDefault, and saying so lets the browser scroll without waiting
    // on us.
    const opts = { passive: true };
    events.forEach((event) => window.addEventListener(event, resetTimer, opts));

    resetTimer();

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer, opts));
      clearTimeout(timer.current);
    };
    // Re-arms whenever the configured duration changes — the whole point of
    // making it configurable.
  }, [effectiveMs, signOut]);

  return children;
}
